// Can the browser still read a file the person added? A File from a picker or a drop is a SNAPSHOT: once the
// file on disk changes (an app such as JPEGmini compressing it in place, a rename, a move) or isn't really on
// this computer (iCloud Drive / Dropbox "online-only" placeholders), every read of it fails — NotReadableError
// / NotFoundError — and no retry or re-mint can fix that: only adding the files again (once they're settled and
// local) does. Without this check such a file reached the preview-copy resize first and failed there as
// "Couldn't make a preview copy in this browser", which pointed at the browser instead of the files.

/** Upload a batch this many files deep into nothing but unreadable files and it stops (nothing will read). */
export const UNREADABLE_STOP_AFTER = 20;

/** The read error's name (e.g. "NotReadableError"), or null when the file can be read. Reads one byte. */
export async function readError(file: Blob): Promise<string | null> {
  try {
    const one = file.slice(0, 1);
    // Blob.arrayBuffer() is Safari 14+; older engines (and jsdom) read through a FileReader.
    await (typeof one.arrayBuffer === "function"
      ? one.arrayBuffer()
      : new Promise<unknown>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = () => reject(r.error);
          r.readAsArrayBuffer(one);
        }));
    return null;
  } catch (e) {
    // DOMException isn't an Error subclass in every engine: read its name directly.
    const err = e as { name?: unknown; message?: unknown } | null;
    return (err && typeof err.name === "string" && err.name) || (err && typeof err.message === "string" && err.message) || String(e) || "read error";
  }
}

/** The reason shown on a file's row. */
export const unreadableReason = (name: string) =>
  `This file can't be read any more — changed, moved or renamed since it was added, or not stored on this computer (iCloud Drive or Dropbox online-only)? (${name})`;

/** The rows a stopped batch never tried. */
export const NOT_TRIED_UNREADABLE = "Not tried: the files before it couldn't be read — add the folder again";

/** What we tell the person, once per batch (and at the summary when the files already can't be read). */
export const UNREADABLE_NOTICE = {
  title: "Your browser can't read these files",
  description:
    "They changed after you added them (still being compressed, renamed or moved?), or they aren't stored on this computer (iCloud Drive or Dropbox “online-only”). Let them finish and make sure they're on this computer, then add the folder again — retrying can't re-read them.",
  stopped: "Nothing was uploaded from the rest of this batch.",
};

/** Indices spread over 0..n-1: the first, the last and evenly between (at most `k`). */
export const spread = (n: number, k: number): number[] => {
  if (n <= 0 || k <= 0) return [];
  if (n <= k) return Array.from({ length: n }, (_, i) => i);
  const out = new Set<number>();
  for (let j = 0; j < k; j += 1) out.add(Math.round((j * (n - 1)) / (k - 1 || 1)));
  return [...out].sort((a, b) => a - b);
};

/** Read one byte of up to `k` files spread over the selection. */
export async function sampleUnreadable(files: Blob[], k = 8): Promise<{ checked: number; unreadable: number; name?: string }> {
  const idx = spread(files.length, k);
  const errs = await Promise.all(idx.map((i) => readError(files[i])));
  const bad = errs.filter((e): e is string => !!e);
  return { checked: idx.length, unreadable: bad.length, name: bad[0] };
}
